import { baseUrl } from "util/APIBaseUrl";
import getRequest from "util/APIHelperGet";
import postRequest from "util/APIHelperPost";
import putRequest from "util/APIHelperPut";

/**
 * Get attendance details for an employee
 * @param {string} employeeId - The employee ID
 * @returns {Promise<Object>} Attendance details object
 */
export const getAttendanceDetails = async (employeeId) => {
  const { payload } = await getRequest(`${baseUrl}/v2/employee/${employeeId}`);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to fetch attendance details");
  }
  return payload;
};

/**
 * Get real-time ticker data for an employee
 * @param {string} employeeId - The employee ID
 * @returns {Promise<Object>} Ticker data with current time, check-in status, and shift details
 */
export const getTimesheetTicker = async (employeeId) => {
  const request = `${baseUrl}/timesheets/${employeeId}/ticker`;
  const { payload } = await getRequest(request);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to fetch ticker data");
  }
  return payload?.data || payload;
};

/**
 * Clock in or check in for an employee
 * @param {string} employeeId - The employee ID
 * @param {Object} data - Clock entry data
 * @param {string} data.action - Action type (e.g., "check_in", "check_out", "break_start", "break_end", "nursing_break_start", "nursing_break_end")
 * @returns {Promise<Object>} Clock entry response
 */
export const clockEntry = async (employeeId, data, checkInRevamped = true) => {
  const request = checkInRevamped
    ? `${baseUrl}/v2/timesheets/${employeeId}/clock-entry`
    : `${baseUrl}/timesheets/${employeeId}/clock-entry`;
  const { payload } = await postRequest(request, JSON.stringify(data));
  if (payload?.error) {
    const error = new Error(payload?.error[0]?.description || "Failed to perform clock action");
    error.errorCode = payload?.error[0]?.errorCode;
    error.fullError = payload;
    throw error;
  }
  return payload;
};

/**
 * Get work schedule for an employee (API v2)
 * @param {string} employeeId - The employee ID
 * @returns {Promise<Object>} Schedule data with timezone and schedule array
 */
export const getEmployeeSchedule = async (employeeId, checkInRevamped = true) => {
  const request = checkInRevamped
    ? `${baseUrl}/v2/timesheets/${employeeId}/schedule`
    : `${baseUrl}/timesheets/${employeeId}/schedule`;
  const { payload } = await getRequest(request);
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to fetch schedule");
  }
  return payload?.data || null;
};

/**
 * Set schedule for an employee (API v2) - POST request for creating new schedule
 * @param {string} employeeId - The employee ID
 * @param {Object} scheduleData - Schedule data
 * @param {string} scheduleData.timezone - Timezone string (e.g., "America/Lima")
 * @param {Array} scheduleData.schedule - Array of schedule objects with dayOfWeek, startTime, endTime
 * @param {boolean} checkInRevamped - Whether to use v2 API
 * @returns {Promise<Object>} Schedule response
 */
export const setEmployeeSchedule = async (employeeId, scheduleData, checkInRevamped = true) => {
  const request = checkInRevamped
    ? `${baseUrl}/v2/timesheets/${employeeId}/schedule`
    : `${baseUrl}/timesheets/${employeeId}/schedule`;
  const { payload } = await postRequest(request, JSON.stringify(scheduleData));
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to set schedule");
  }
  return payload;
};

/**
 * Update schedule for an employee (API v2) - PUT request for updating existing schedule
 * @param {string} employeeId - The employee ID
 * @param {Object} scheduleData - Schedule data
 * @param {string} scheduleData.timezone - Timezone string (e.g., "America/Lima")
 * @param {Array} scheduleData.schedule - Array of schedule objects with dayOfWeek, startTime, endTime
 * @param {boolean} checkInRevamped - Whether to use v2 API
 * @returns {Promise<Object>} Schedule response
 */
export const updateEmployeeSchedule = async (employeeId, scheduleData, checkInRevamped = true) => {
  const path = checkInRevamped ? `/v2/timesheets/${employeeId}/schedule` : `/timesheets/${employeeId}/schedule`;
  const { payload } = await putRequest({
    path,
    body: scheduleData,
    stringifyBody: true,
  });
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to update schedule");
  }
  return payload;
};

/**
 * Record missed entry for an employee (missed clock-out or missed break)
 * @param {string} employeeId - The employee ID
 * @param {string} entryTime - Entry time in format "YYYY-MM-DD HH:mm" (e.g., "2025-01-15 17:00")
 * @param {string} actionType - Action type: "checkout" or "break_start" or "break_end"
 * @returns {Promise<Object>} Response from missed entry API
 */
export const recordMissedEntry = async (employeeId, entryTime, actionType) => {
  const request = `${baseUrl}/timesheets/${employeeId}/missed-entry`;
  const { payload } = await postRequest(request, JSON.stringify({ entryTime, actionType }));
  if (payload?.error) {
    throw new Error(payload?.error[0]?.description || "Failed to record missed entry");
  }
  return payload;
};
